// packages import
import { createContext, FC, Reducer, useCallback, useContext, useEffect, useMemo, useReducer } from 'react'
import { useSearchParams } from 'react-router-dom'

// others
import { useFetchInterestEntriesLazyQuery } from '../../generated'
import { GRAPHQL_QUERY_POLICY } from '../constants'
import { ChildrenType } from '../interfaceTypes'
import {
  InterestEntriesAction,
  InterestEntriesActionType,
  interestEntriesInitialState,
  interestEntriesReducer,
  InterestEntriesState,
  InterestEntriesFilters,
  InterestEntriesSortBy,
  InterestEntriesSortDir
} from '../components/billing/interestEntries/interestEntriesReducer'
import { AuthContext } from './AuthContext'
import { AppContext } from './AppContext'
import { ActionType } from './AppContextReducer'

const PAGE_LIMIT = 10

export interface InterestEntriesContextType extends InterestEntriesState {
  dispatch: React.Dispatch<InterestEntriesAction>
  filters: InterestEntriesFilters
  refetch: () => void
  setFilters: (filters: InterestEntriesFilters) => void
}

export const InterestEntriesContext = createContext<InterestEntriesContextType>({
  ...interestEntriesInitialState,
  filters: {
    sortBy: InterestEntriesSortBy.Date,
    sortDir: InterestEntriesSortDir.Desc
  },
  dispatch: () => {
    return
  },
  refetch: () => {
    return
  },
  setFilters: () => {
    return
  }
})

export const InterestEntriesContextProvider: FC<ChildrenType> = ({ children }): JSX.Element => {
  const { isLoggedIn } = useContext(AuthContext)
  const { dispatch: appDispatch } = useContext(AppContext)
  const [state, dispatch] = useReducer<Reducer<InterestEntriesState, InterestEntriesAction>>(
    interestEntriesReducer,
    interestEntriesInitialState
  )
  const [searchParams, setSearchParams] = useSearchParams()

  const filters = useMemo<InterestEntriesFilters>(() => {
    const billIdRaw = searchParams.get('billId')
    const billId = billIdRaw ? Number(billIdRaw) : undefined
    const clientId = searchParams.get('clientId') || undefined
    const caseId = searchParams.get('caseId') || undefined
    const fromDate = searchParams.get('fromDate') || undefined
    const toDate = searchParams.get('toDate') || undefined
    const sortBy = (searchParams.get('sortBy') as InterestEntriesSortBy) || InterestEntriesSortBy.Date
    const sortDir = (searchParams.get('sortDir') as InterestEntriesSortDir) || InterestEntriesSortDir.Desc

    return {
      billId: Number.isFinite(billId) ? billId : undefined,
      clientId,
      caseId,
      fromDate,
      toDate,
      sortBy,
      sortDir
    }
  }, [searchParams])

  const currentPage = useMemo(() => parseInt(searchParams.get('currentPage') || '1'), [searchParams])

  const [fetchInterestEntriesQuery, { loading, refetch }] = useFetchInterestEntriesLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError() {
      dispatch({ type: InterestEntriesActionType.SET_INTEREST_ENTRIES, interestEntries: [] })
      dispatch({ type: InterestEntriesActionType.SET_LOADING, loading: false })
      appDispatch({ type: ActionType.SET_IS_LOADING, isLoading: false })
    },
    onCompleted(data) {
      if (data?.fetchInterestEntries) {
        const { interestEntries, pagination } = data.fetchInterestEntries

        dispatch({
          type: InterestEntriesActionType.SET_INTEREST_ENTRIES,
          interestEntries: interestEntries || []
        })

        if (pagination) {
          const { totalPages, totalCount } = pagination
          totalPages && appDispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: totalPages })
          totalCount && appDispatch({ type: ActionType.SET_TOTAL_DATA_COUNT, totalDataCount: totalCount })
        }

        dispatch({ type: InterestEntriesActionType.SET_LOADING, loading: false })
        appDispatch({ type: ActionType.SET_IS_LOADING, isLoading: false })
      }
    }
  })

  useEffect(() => {
    if (isLoggedIn) {
      dispatch({ type: InterestEntriesActionType.SET_LOADING, loading: true })

      fetchInterestEntriesQuery({
        variables: {
          fetchInterestEntriesInput: {
            page: currentPage,
            limit: PAGE_LIMIT,
            billId: filters.billId,
            clientId: filters.clientId,
            caseId: filters.caseId,
            fromDate: filters.fromDate,
            toDate: filters.toDate,
            sortBy: filters.sortBy,
            sortDir: filters.sortDir
          }
        }
      })
    }
  }, [filters, currentPage, isLoggedIn, appDispatch, fetchInterestEntriesQuery])

  useEffect(() => {
    appDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage })

    return () => {
      appDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
    }
    // eslint-disable-next-line
  }, [])

  const setFilters = useCallback(
    (newFilters: InterestEntriesFilters) => {
      setSearchParams(params => {
        const setOrDelete = (key: string, value?: string | number) => {
          if (value !== undefined && value !== null && value !== '') {
            params.set(key, String(value))
          } else {
            params.delete(key)
          }
        }

        if ('billId' in newFilters) setOrDelete('billId', newFilters.billId)
        if ('clientId' in newFilters) setOrDelete('clientId', newFilters.clientId)
        if ('caseId' in newFilters) setOrDelete('caseId', newFilters.caseId)
        if ('fromDate' in newFilters) setOrDelete('fromDate', newFilters.fromDate)
        if ('toDate' in newFilters) setOrDelete('toDate', newFilters.toDate)
        if ('sortBy' in newFilters) setOrDelete('sortBy', newFilters.sortBy)
        if ('sortDir' in newFilters) setOrDelete('sortDir', newFilters.sortDir)

        params.set('currentPage', '1')
        appDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })

        return params
      })
    },
    [setSearchParams, appDispatch]
  )

  const contextValue = useMemo(
    () => ({
      ...state,
      filters,
      loading: loading || state.loading,
      dispatch,
      refetch,
      setFilters
    }),
    [state, loading, refetch, setFilters, filters]
  )

  return <InterestEntriesContext.Provider value={contextValue}>{children}</InterestEntriesContext.Provider>
}
